import React, { useMemo, useState } from "react";
import { Check, Copy } from "feather-icons-react";
import { formatDate, isEmptyValue, toYN } from "../viewQuoteSubmission/utils/formatters";

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-300">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {children}
      </div>
    </div>
  );
}

function CopyButton({ label, value }) {
  const [copied, setCopied] = useState(false);
  const displayValue = value?.toString() || "N/A";

  const handleCopy = async () => {
    await navigator.clipboard.writeText(displayValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={handleCopy}
          className="flex-shrink-0 p-2 rounded-md hover:bg-gray-100 transition-colors"
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-600" />
          ) : (
            <Copy className="w-4 h-4 text-gray-600" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            {label}
          </p>
          <p className="text-sm font-semibold text-gray-900 break-all">
            {displayValue}
          </p>
        </div>
      </div>
    </div>
  );
}

const formatList = (value) => {
  if (!value) return "N/A";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "N/A";
  return value;
};

function Field({ label, value, format }) {
  let display = value;

  if (format === "date") {
    display = formatDate(value);
  } else if (format === "yn") {
    display = toYN(value);
  } else if (format === "list") {
    display = formatList(value);
  } else {
    display = isEmptyValue(value) ? "N/A" : value;
  }

  return <CopyButton label={label} value={display} />;
}

const getValue = (obj, path) =>
  path.split(".").reduce((acc, key) => acc?.[key], obj);

function renderFields(fields, source) {
  return fields
    .filter((field) =>
      typeof field.show === "function"
        ? field.show(source)
        : field.show !== false
    )
    .map((field) => {
      const value = field.getValue
        ? field.getValue(source)
        : field.path
        ? getValue(source, field.path)
        : field.value;
      return (
        <Field
          key={field.key || field.label}
          label={field.label}
          value={value}
          format={field.format}
        />
      );
    });
}

function Note({ children }) {
  return (
    <div className="col-span-full rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
      {children}
    </div>
  );
}

export function PersonalQuoteView({ quoteData }) {
  const quote = quoteData?.quote || {};
  const policyType = quoteData?.policy_type || quote?.step1?.quoteType;
  const isRenters = policyType === "renters";

  const initialFields = [
    { label: "Producer", path: "step1.producer" },
    { label: "Agent Email", path: "step1.agentEmail" },
    { label: "Policy Effective Date", path: "step1.policyDate", format: "date" },
    { label: "Insured State", path: "step1.insuredState" },
    { label: "Carriers", path: "step1.carrier", format: "list" },
  ];

  const insuredFields = [
    { label: "First Name", path: "step2.firstName" },
    { label: "Last Name", path: "step2.lastName" },
    { label: "Date of Birth", path: "step2.dateOfBirth", format: "date" },
    { label: "Gender", path: "step2.gender" },
    { label: "Insured Email", path: "step2.email" },
    { label: "Insured Phone Number", path: "step2.phone" },
  ];

  const addressFields = [
    { label: "Address", path: "step2.address1" },
    { label: "Address Line 2 (Apt #, etc.)", path: "step2.address2" },
    { label: "City", path: "step2.city" },
    { label: "State", path: "step2.state" },
    { label: "Zip", path: "step2.zip" },
    {
      label: "Has the insured moved in the past 2 months?",
      path: "step2.movedRecently",
      format: "yn",
    },
  ];

  const priorAddressFields = [
    { label: "Prior Mailing Address", path: "step2.priorAddress" },
    { label: "Prior City", path: "step2.priorCity" },
    { label: "Prior State", path: "step2.priorState" },
    { label: "Prior Zip", path: "step2.priorZip" },
  ];

  const rentersResidenceFields = [
    { label: "Residence Type", path: "step2.residenceType" },
    {
      label: "Does the insured have restricted dogs?",
      path: "step2.hasRestrictedDogs",
      format: "yn",
    },
    { label: "Personal Property Amount", path: "step2.personalPropertyAmount" },
  ];

  const rentersDemographicFields = [
    { label: "First Name", path: "step2.demographicFirstName" },
    { label: "Last Name", path: "step2.demographicLastName" },
    { label: "Marital Status", path: "step2.demographicMaritalStatus" },
    { label: "Date of Birth", path: "step2.demographicDateOfBirth", format: "date" },
    { label: "Gender", path: "step2.demographicGender" },
  ];

  const rentersAdditionalFields = [
    { label: "First Name", path: "step2.additionalFirstName" },
    { label: "Last Name", path: "step2.additionalLastName" },
    { label: "Marital Status", path: "step2.additionalMaritalStatus" },
    { label: "Relationship", path: "step2.additionalRelationship" },
    {
      label: "Should this person be listed as the 2nd named insured?",
      path: "step2.additionalSecondNamedInsured",
      format: "yn",
    },
    { label: "Date of Birth", path: "step2.additionalDateOfBirth", format: "date" },
    { label: "Gender", path: "step2.additionalGender" },
  ];

  const historyFields = isRenters
    ? [
        {
          label: "Prior Renters insurance carrier",
          path: "step3.priorCarrier",
        },
        { label: "Prior Renters Liability Limit", path: "step3.priorLiabilityLimit" },
        {
          label: "Reported claims past 3 years",
          path: "step3.reportedClaims",
        },
      ]
    : [
        {
          label: "Previously vehicle liability",
          path: "step5.priorInsuranceStatus",
        },
        { label: "Prior Insurance Carrier", path: "step5.priorInsuranceCarrier" },
        {
          label: "Previous bodily injury limits",
          path: "step5.bodilyInjuryLimits",
        },
        {
          label: "Time with previous carrier",
          path: "step5.yearsWithCarrier",
        },
        {
          label: "All drivers listed?",
          path: "step5.driversListed",
          format: "yn",
        },
        { label: "How many PIP claims in the past 5 years?", path: "step5.pipClaims" },
        {
          label: "Number of residents",
          path: "step5.totalResidents",
        },
        { label: "Primary Residence", path: "step5.primaryResidence" },
        {
          label: "Multi-policy discount",
          path: "step5.rentersDiscount",
          format: "yn",
        },
      ];

  const coverageFields = isRenters
    ? [
        { label: "Liability Limit", path: "step4.liabilityLimit" },
        { label: "Medical Payments Limit", path: "step4.medicalPaymentsLimit" },
        { label: "Deductible", path: "step4.deductible" },
        { label: "Increased Sublimit", path: "step4.theftSublimit" },
        { label: "Home Computer Coverage", path: "step4.homeComputerCoverage" },
        { label: "Payment Plan", path: "step4.paymentPlan" },
        {
          label: "Run Clue?",
          path: "step4.runClue",
          format: "yn",
        },
      ]
    : [
        { label: "Bodily Injury & Property Damage", path: "step6.bodilyInjury" },
        {
          label:
            "Uninsured/Underinsured Motorist Bodily Injury (matches BI limit if selected)",
          path: "step6.umbi",
          format: "yn",
        },
        {
          label: "Uninsured/Underinsured Motorist Property Damage",
          path: "step6.umPropertyDamage",
        },
        { label: "Med Pay", path: "step6.medPay" },
        { label: "PIP", path: "step6.pip" },
        { label: "Comp Deductible", path: "step6.compDeductible" },
        { label: "Collision Deductible", path: "step6.collisionDeductible" },
        { label: "Payment Plan", path: "step6.paymentPlan" },
        {
          label: "Run Clue/MVR?",
          path: "step6.runClue",
          format: "yn",
        },
      ];

  const vehicles = useMemo(() => quote?.step3?.vehicles || [], [quote]);
  const drivers = useMemo(() => quote?.step4?.drivers || [], [quote]);

  return (
    <div className="space-y-8">
      <Section title="Initial Information">{renderFields(initialFields, quote)}</Section>

      <Section title="Insured">{renderFields(insuredFields, quote)}</Section>

      <Section title={isRenters ? "Mailing Address" : "Insured Address"}>
        {renderFields(addressFields, quote)}
      </Section>

      {quote?.step2?.movedRecently && (
        <Section title="Prior Mailing Address">
          {renderFields(priorAddressFields, quote)}
        </Section>
      )}

      {isRenters && (
        <Section title="Residence Details">
          {renderFields(rentersResidenceFields, quote)}
          <Note>
            Akitas, American Bulldogs, Chow Chow, Doberman Pinschers, Mastiffs, Pit Bulls,
            Rottweilers, Staffordshire Terriers, Wolf Hybrids, or any mix or variation of these
            breeds, or any dog with a history of biting, or any dog underwriting deems ineligible
          </Note>
        </Section>
      )}

      {isRenters && (
        <Section title="Demographic">
          {renderFields(rentersDemographicFields, quote)}
        </Section>
      )}

      {isRenters && (
        <Section title="Additional Person">
          {renderFields(rentersAdditionalFields, quote)}
        </Section>
      )}

      {!isRenters && (
        <Section title="Vehicles">
          {vehicles.length === 0 ? (
            <div className="col-span-full text-sm text-gray-500">No vehicles added.</div>
          ) : (
            vehicles.map((vehicle, idx) => (
              <div key={`vehicle-${idx}`} className="col-span-full">
                <Section title={`Vehicle ${idx + 1}`}>
                  {renderFields(
                    [
                      { label: "VIN", path: "vin" },
                      { label: "Year", path: "year" },
                      { label: "Make", path: "make" },
                      { label: "Model", path: "model" },
                      { label: "Garaging Zip", path: "garagingZip" },
                      { label: "Ownership Years", path: "ownershipYears" },
                      { label: "Ownership Months", path: "ownershipMonths" },
                      { label: "Primary Vehicle Use", path: "primaryUse" },
                      {
                        label: "Other Primary Use",
                        path: "primaryUseOther",
                        show: (src) => !!src?.primaryUseOther,
                      },
                      {
                        label: "Is the vehicle used for ride share? (Uber, Doordash, etc.)",
                        path: "rideShare",
                        format: "yn",
                      },
                      {
                        label: "Is the vehicle used for delivery? (Uber Eats, Amazon etc.)",
                        path: "delivery",
                        format: "yn",
                      },
                      { label: "Annual Miles", path: "annualMiles" },
                      { label: "Lienholder", path: "lienholder", format: "yn" },
                      {
                        label: "APD Deductible Selected",
                        path: "apdCoverage",
                        format: "yn",
                      },
                      {
                        label: "APD Deductible Amount",
                        getValue: (src) =>
                          src?.apdCoverage ? src?.apdDeductible || "N/A" : "N/A",
                      },
                      {
                        label: "Lienholder Name",
                        path: "lienholderName",
                        show: (src) => !!src?.lienholder,
                      },
                      {
                        label: "Lienholder Address",
                        path: "lienholderAddress",
                        show: (src) => !!src?.lienholder,
                      },
                      {
                        label: "STE/Floor (optional)",
                        path: "lienholderAddress2",
                        show: (src) => !!src?.lienholder,
                      },
                      {
                        label: "Lienholder City",
                        path: "lienholderCity",
                        show: (src) => !!src?.lienholder,
                      },
                      {
                        label: "Lienholder State",
                        path: "lienholderState",
                        show: (src) => !!src?.lienholder,
                      },
                      {
                        label: "Lienholder Zip",
                        path: "lienholderZip",
                        show: (src) => !!src?.lienholder,
                      },
                    ],
                    vehicle
                  )}
                </Section>
              </div>
            ))
          )}
        </Section>
      )}

      {!isRenters && (
        <Section title="Drivers">
          {drivers.length === 0 ? (
            <div className="col-span-full text-sm text-gray-500">No drivers added.</div>
          ) : (
            drivers.map((driver, idx) => (
              <div key={`driver-${idx}`} className="col-span-full">
                <Section title={`Driver ${idx + 1}`}>
                  {renderFields(
                    [
                      { label: "First Name", path: "firstName" },
                      { label: "Last Name", path: "lastName" },
                      { label: "Marital Status", path: "maritalStatus" },
                      { label: "Date of Birth", path: "dateOfBirth", format: "date" },
                      { label: "Gender", path: "gender" },
                      { label: "Highest Level of Education", path: "educationLevel" },
                      { label: "License Type", path: "licenseType" },
                      { label: "Does Driver Require an SR22?", path: "sr22", format: "yn" },
                      { label: "How long has the driver been licensed?", path: "licensedDuration" },
                      { label: "Employment Type", path: "employmentType" },
                      {
                        label: "What line of work is the insured in?",
                        path: "employmentLine",
                        show: (src) => !!src?.employmentLine,
                      },
                      { label: "Auto Driver Status", path: "autoDriverStatus" },
                      { label: "Distant Student Discount?", path: "distantStudentDiscount", format: "yn" },
                      { label: "Good Student Discount?", path: "goodStudentDiscount", format: "yn" },
                      { label: "Driver's License", path: "driverLicense" },
                    ],
                    driver
                  )}
                </Section>
              </div>
            ))
          )}
        </Section>
      )}

      <Section title="History">{renderFields(historyFields, quote)}</Section>

      {!isRenters && (
        <div className="grid grid-cols-1 gap-4">
          <Note>
            PLEASE NOTE: All personal auto policies with Cover Badger are required to be paperless
          </Note>
        </div>
      )}

      {isRenters && (
        <div className="grid grid-cols-1 gap-3">
          <Note>ALL RENTERS POLICIES WITH WRITTEN BY COVER BADGER WILL BE E-SIGNATURE</Note>
          <Note>
            ALL RENTERS POLICIES WITH WRITTEN BY COVER BADGER WILL HAVE PAPERLESS BILLING VIA EMAIL
          </Note>
        </div>
      )}

      <Section title="Coverages">{renderFields(coverageFields, quote)}</Section>
    </div>
  );
}
